import React, {
  ChangeEvent,
  CSSProperties,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Button,
  Confirm,
  Dropdown,
  Form,
  FormField,
  FormGroup,
  Icon,
  Image,
  Input,
  InputProps,
  Modal,
} from "semantic-ui-react";

import moment from "moment";
import { useIntl } from "react-intl";

import Android12Switch from "react-lib/appcon/common/Android12Switch";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

import DateTextBox from "react-lib/apps/common/DateTextBox";
import MaskedInput from "react-lib/apps/common/MaskedInput";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import TreeViewSearchBox, {
  TreeNode,
  TreeViewSearchBoxProps,
} from "react-lib/apps/common/TreeViewSearchBox";

import { convertBase64ToFile } from "react-lib/apps/HISV3/common/CommonInterface";
import WebcamProfile from "react-lib/apps/HISV3/common/WebcamProfile";

import { getAge, getRequireKeyError } from "react-lib/apps/USER/CardSettingEmployee";
import ModChangePassword from "react-lib/apps/USER/ModChangePassword";

import CardEmployeeInformationUX from "./CardEmployeeInformationUX";

import { getDivisionHierarchy, getDivisionParentName } from "../common/sequence/AppInitialize";
import {
  ACTIONS,
  BTN_ACTS,
  EDUCATION_LEVEL_OPTIONS,
  IDENTITY_DOCUMENT_OPTIONS,
  JOB_LEVEL_OPTIONS,
  MONTH_OPTIONS,
  MasterData,
  MasterOptions,
  OnEvent,
  PickedState,
  RequiredFieldType,
  RunSequence,
  SEQUENCE_NAME,
  SetProp,
  State,
  STATUS_OPTIONS,
} from "./sequence/EmployeeInfo";

import {
  beStringToMoment,
  convertAdBeDateToMoment,
  formatDate,
  isValidAdBeDate,
} from "react-lib/utils/dateUtils";

type CardEmployeeInfoProps = Pick<
  PickedState,
  "buttonLoadCheck" | "changePassword" | "errorMessage" | "successMessage"
> & {
  onEvent: OnEvent;
  setProp: SetProp;
  runSequence: RunSequence;
  EmployeeInfoSequence: State["EmployeeInfoSequence"];
  employeeId?: number;
  readOnly?: boolean;
  userId?: number;
  allDivisionGroup: Record<string, any>[];
  django?: any;
  languageUX: string;
  masterData: MasterData;
  masterOptions: MasterOptions;
};

type Styles = Record<"boxProfile" | "profile" | "switchStyle", CSSProperties>;

const IMAGES = {
  profile: "/static/images/person.png",
};

const styles: Styles = {
  boxProfile: {
    alignItems: "center",
    border: "solid rgba(148, 148, 148, 1) 1px",
    display: "flex",
    height: "160px",
    marginLeft: "1.15rem",
    overflow: "hidden",
    width: "120px",
  },
  profile: { marginRight: "10px", objectFit: "contain", width: "120px" },
  switchStyle: { marginRight: "-3px", transform: "scale(0.7)" },
};

const SECTION_NAME_LIST = {
  "Address on ID Card": "Address on ID Card",
  "Basic Employee Information": "Basic Employee Information",
  "Contact Information": "Contact Information",
  "Create a user for system access": "Create a user for system access",
  "Employment Information": "Employment Information",
  "Personal Information": "Personal Information",
} as const;

const CURRENT_DATE = formatDate(moment());

const CardEmployeeInformation = (props: CardEmployeeInfoProps) => {
  const intl = useIntl();

  const [affiliateItems, setAffiliateItems] = useState<TreeNode[]>([]);
  const [showRequiredField, setShowRequiredField] = useState<RequiredFieldType>({});
  const [showRequiredEducationField, setShowRequiredEducationField] = useState<Record<string, string>>({});
  const [showRequiredExperienceField, setShowRequiredExperienceField] = useState<Record<string, string>>({});

  const [openWebCam, setOpenWebCam] = useState<boolean>(false);
  const [openResetPassword, setOpenResetPassword] = useState<boolean>(false);

  const [showEmailChangeModal, setShowEmailChangeModal] = useState<boolean>(false);
  const [showEducationInfoModal, setShowEducationInfoModal] = useState<boolean>(false);
  const [showDeleteEducationConfirm, setShowDeleteEducationConfirm] = useState<boolean>(false);
  const [deleteEducationId, setDeleteEducationId] = useState<number | null>(null);
  const [isSavingEducation, setIsSavingEducation] = useState(false);
  const [showExperienceInfoModal, setShowExperienceInfoModal] = useState<boolean>(false);
  const [showDeleteExperienceConfirm, setShowDeleteExperienceConfirm] = useState<boolean>(false);
  const [deleteExperienceId, setDeleteExperienceId] = useState<number | null>(null);
  const [isSavingExperience, setIsSavingExperience] = useState(false);
  const [currentLoadingButton, setCurrentLoadingButton] = useState<string | null>(null);
  const [successButton, setSuccessButton] = useState<string | null>(null);

  const [pendingEducation, setPendingEducation] = useState<any[]>([]);
  const [pendingExperience, setPendingExperience] = useState<any[]>([]);

  const [hasShownPendingAlert, setHasShownPendingAlert] = useState<boolean>(false);
  const [showPendingAlert, setShowPendingAlert] = useState<boolean>(false);
  const [pendingAction, setPendingAction] = useState<'education' | 'experience' | null>(null);

  const [educationData, setEducationData] = useState({
    id: null,
    index: 0,
    graduationStatus: "STUDYING",
    graduationYear: null,
    educationLevel: "",
    category: null,
    institution: "",
    faculty: "",
    major: "",
    degreeName: "",
  });

  const [experienceData, setExperienceData] = useState({
    id: null,
    index: 0,
    jobLevel: "",
    startMonth: null,
    startYear: null,
    endMonth: null,
    endYear: null,
    companyName: "",
    companyAddress: "",
    position: "",
    jobType: "",
    jobDescription: "",
  });

  const graduationYearOptions = useMemo(() => {
    const birthDateStr = props.EmployeeInfoSequence?.basic?.birth_date;

    const birthYear = (birthDateStr ? new Date(birthDateStr).getFullYear() : new Date().getFullYear() - 60) + 543;
    const currentYear = new Date().getFullYear() + 543;

    return Array.from({ length: currentYear - birthYear + 1 }, (_, i) => {
      const year = birthYear + i;
      return { key: year, text: `${year}`, value: year };
    }).reverse();
  }, [props.EmployeeInfoSequence?.basic?.birth_date]);

  const experienceYearOptions = useMemo(() => {
    const birthDateStr = props.EmployeeInfoSequence?.basic?.birth_date;

    const birthYear = (birthDateStr ? new Date(birthDateStr).getFullYear() : new Date().getFullYear() - 60) + 543;
    const currentYear = new Date().getFullYear() + 543;

    return Array.from({ length: currentYear - birthYear + 1 }, (_, i) => {
      const year = birthYear + i;
      return { key: year, text: `${year}`, value: year };
    }).reverse();
  }, [props.EmployeeInfoSequence?.basic?.birth_date]);

  const educationCategoryOptions = useMemo(() => {
    const items = props.EmployeeInfoSequence?.clinicalTermSet?.edu_category?.items || [];

    return items.map((item: any) => ({
      key: item.id,
      text: item.name,
      value: item.id,
    }));
  }, [props.EmployeeInfoSequence]);

  const handleEducationChange = (field: string) => (e: any, data: any) => {
    const value = data.value;
    setEducationData((prev) => ({ ...prev, [field]: value }));
  };

  const handleExperienceChange = (field: string) => (e: any, data: any) => {
    const value = data.value;
    setExperienceData((prev) => ({ ...prev, [field]: value }));
  };
  
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setHasShownPendingAlert(false);
    props.runSequence({
      sequence: "EmployeeInfo",
      employeeId: props.employeeId,
      restart: true,
      userId: props.userId,
    });

    return () => {
      props.setProp(`errorMessage`, {
        ...props.errorMessage,
        [BTN_ACTS.SAVE]: null,
        [SEQUENCE_NAME]: null,
      });
    };
  }, []);

  useEffect(() => {
    const items = props.EmployeeInfoSequence?.affiliateItems ?? [];

    setAffiliateItems(items as TreeNode[]);
  }, [props.EmployeeInfoSequence?.affiliateItems]);

  useEffect(() => {
    const error = props.errorMessage?.[BTN_ACTS.SAVE];

    if (error) {
      if ("non_field_errors" in error) {
        props.setProp(`errorMessage.${SEQUENCE_NAME}`, error);

        return;
      }

      setShowRequiredField(error);
    }
  }, [props.errorMessage?.[BTN_ACTS.SAVE]]);

  useEffect(() => {
    const isNoneDoctor = props.EmployeeInfoSequence?.employment?.isNoneDoctor;
    const licence = showRequiredField.licence;

    if (licence && !isNoneDoctor) {
      setShowRequiredField((prev) => ({ ...prev, licence: "" }));
    }
  }, [props.EmployeeInfoSequence?.employment?.isNoneDoctor]);

  const checkIsSingle = useCallback(
    (maritalStatus?: string | null) => maritalStatus === "SINGLE",
    []
  );

  useEffect(() => {
    if (!showEmailChangeModal) {
      setCurrentLoadingButton(null);
      setSuccessButton(null);
    }
  }, [showEmailChangeModal]);

  useEffect(() => {
    const saveStatus = props.buttonLoadCheck?.[BTN_ACTS.EDUCATION_SAVE];
    if (isSavingEducation && saveStatus === "SUCCESS") {
      setIsSavingEducation(false);
      setEducationData({
        id: null,
        index: 0,
        graduationStatus: "STUDYING",
        graduationYear: null,
        educationLevel: "",
        category: null,
        institution: "",
        faculty: "",
        major: "",
        degreeName: "",
      });
      setShowEducationInfoModal(false);
    }
  }, [props.buttonLoadCheck?.[BTN_ACTS.EDUCATION_SAVE], isSavingEducation]);

  useEffect(() => {
    const saveStatus = props.buttonLoadCheck?.[BTN_ACTS.EXPERIENCE_SAVE];
    if (isSavingExperience && saveStatus === "SUCCESS") {
      setIsSavingExperience(false);
      setExperienceData({
        id: null,
        index: 0,
        jobLevel: "",
        startMonth: null,
        startYear: null,
        endMonth: null,
        endYear: null,
        companyName: "",
        companyAddress: "",
        position: "",
        jobType: "",
        jobDescription: "",
      });
      setShowExperienceInfoModal(false);
    }
  }, [props.buttonLoadCheck?.[BTN_ACTS.EXPERIENCE_SAVE], isSavingExperience]);

  useEffect(() => {
    const savePending = async () => {
      const employeeSaveStatus = props.buttonLoadCheck?.[BTN_ACTS.SAVE];
      if (employeeSaveStatus === "SUCCESS" && props.employeeId) {
        try {
          for (const edu of pendingEducation) {
            await props.runSequence({
              sequence: "EmployeeInfo",
              action: ACTIONS.EDUCATION_SAVE,
              params: { educationData: edu, employeeId: props.employeeId },
            });
          }
          for (const exp of pendingExperience) {
            await props.runSequence({
              sequence: "EmployeeInfo",
              action: ACTIONS.EXPERIENCE_SAVE,
              params: { experienceData: exp, employeeId: props.employeeId },
            });
          }
          setPendingEducation([]);
          setPendingExperience([]);
        } catch (error) {
          console.error("Failed to save pending data:", error);
        }
      }
    };
    savePending();
  }, [props.buttonLoadCheck?.[BTN_ACTS.SAVE], props.employeeId]);

  const handleChangeTreeview = useCallback(
    (detail: any): TreeViewSearchBoxProps["onChange"] =>
      (data) => {
        const parentName = getDivisionParentName(data.value, props.allDivisionGroup, true);

        props.setProp(`EmployeeInfoSequence.employment`, {
          ...detail,
          affiliate: data.value,
          affiliate_name: parentName,
        });
      },
    [props.allDivisionGroup, props.EmployeeInfoSequence?.employment]
  );

  const handleSearchTreeview = useCallback((data: any) => {
    props.runSequence({
      sequence: "EmployeeInfo",
      action: ACTIONS.AFFILIATE_SEARCH,
      ...data,
    });
  }, []);

  const getPositionFlag = useCallback(
    (positionId: number) =>
      (props.masterData.positionAll ?? []).find((position) => position.id === positionId) ?? {},
    [props.masterData.positionAll]
  );

  const handleParentSearchTreeview = useCallback((data: any) => {
    props.runSequence({
      sequence: "EmployeeInfo",
      action: ACTIONS.AFFILIATE_SEARCH,
      ...data,
    });
  }, []);

  const managementOptions = useMemo(() => {
    const divisions = getDivisionHierarchy(
      props.EmployeeInfoSequence?.employment?.affiliate || null,
      props.allDivisionGroup
    );

    return divisions.map((item) => ({ key: item.id, text: item.name, value: item.id }));
  }, [props.EmployeeInfoSequence?.employment]);

  const calculateAge = useMemo(() => {
    const birthdate = props.EmployeeInfoSequence?.basic?.birth_date;
    const birthdateAd = convertAdBeDateToMoment(birthdate)?.startOf("day");

    return birthdateAd === undefined ? "" : getAge(birthdateAd);
  }, [props.EmployeeInfoSequence?.basic]);

  const isSingle = useMemo(() => {
    const maritalStatus = props.EmployeeInfoSequence?.personal?.marital_status;

    return checkIsSingle(maritalStatus);
  }, [checkIsSingle, props.EmployeeInfoSequence?.personal]);

  const profileImage = useMemo(() => {
    const image = props.EmployeeInfoSequence?.user?.image;

    return image instanceof File ? URL.createObjectURL(image) : image;
  }, [props.EmployeeInfoSequence?.user]);

  const family = useMemo(
    () =>
      (props.EmployeeInfoSequence?.personal?.family ?? []).map((item, index) => ({
        ...item,
        index,
      })),
    [props.EmployeeInfoSequence?.personal?.family]
  );

  const hasEmptyMember = useMemo(
    () =>
      family.some((item) => {
        const isActive = item.active;
        const hasNoId = !item.id;
        const isEmpty = !item.birth_date && !item.cid && !item.name;

        return isActive && hasNoId && isEmpty;
      }),
    [family]
  );

  const filteredChildren = useMemo(
    () => family.filter((item) => item.active && item.type === "CHILD"),
    [family]
  );

  const spouseName = useMemo(
    () => family.find((item) => item.type === "SPOUSE")?.name || "",
    [family]
  );

  const contactDistrictOptions = useMemo(() => {
    const contactAddress = props.EmployeeInfoSequence?.contact_address;
    const district = props.masterOptions.district ?? [];

    return district.filter((item) => item.relate === contactAddress?.province);
  }, [props.EmployeeInfoSequence?.contact_address]);

  const idCardDistrictOptions = useMemo(() => {
    const idCardAddress = props.EmployeeInfoSequence?.id_card_address;
    const district = props.masterOptions.district ?? [];

    return district.filter((item) => item.relate === idCardAddress?.province);
  }, [props.EmployeeInfoSequence?.id_card_address]);

  const contactCityOptions = useMemo(() => {
    const contactAddress = props.EmployeeInfoSequence?.contact_address;
    const city = props.masterOptions.city ?? [];

    return city.filter((item) => item.relate === contactAddress?.district);
  }, [props.EmployeeInfoSequence?.contact_address]);

  const idCardCityOptions = useMemo(() => {
    const idCardAddress = props.EmployeeInfoSequence?.id_card_address;
    const city = props.masterOptions.city ?? [];

    return city.filter((item) => item.relate === idCardAddress?.district);
  }, [props.EmployeeInfoSequence?.id_card_address]);

  const filteredSectionList = useMemo(() => {
    const roleTypes: string[] = props.django?.user?.role_types || [];
    const isHR = roleTypes.includes("HR");

    // กรณีเป็น role type อื่น จะแสดง แค่ 3 ส่วน
    return isHR || !props.django
      ? Object.values(SECTION_NAME_LIST)
      : Object.values(SECTION_NAME_LIST).filter(
          (name) =>
            !["Address on ID Card", "Contact Information", "Personal Information"].includes(name)
        );
  }, [props.django]);

  const validateFieldChanges = (name: string, value: any, detail: any) => {
    // ฟังก์ชันอัพเดทสถานะหมอ/ไม่ใช่หมอ
    const updateDoctorState = (isDoctor: boolean) => {
      detail.employment ??= {};
      detail.employment.isDoctor = isDoctor;
      detail.employment.isNoneDoctor = !isDoctor;

      if (isDoctor) {
        detail.employment.licence = "";

        return;
      }

      detail.employment.doctor_code = "";
      detail.employment.certificate_no = "";
      detail.employment.specialties = [];
    };

    // ถ้าเปลี่ยนสถานะการสมรสเป็นโสด -> ล้างชื่อคู่สมรส
    if (name === "personal.marital_status" && checkIsSingle(value)) {
      detail.personal ??= {};
      detail.personal.spouse_name = "";
    }

    // อัพเดทรหัสไปรษณีย์อัตโนมัติ เมื่อเลือกเขต/แขวง (ที่อยู่บัตรประชาชน)
    if (name === "id_card_address.city") {
      const city = props.masterData.city?.find((item) => item.id === value);

      detail.id_card_address.zipcode = city?.zipcode || "";
    }

    // อัพเดทรหัสไปรษณีย์อัตโนมัติ เมื่อเลือกเขต/แขวง (ที่อยู่ติดต่อ)
    if (name === "contact_address.city") {
      const city = props.masterData.city?.find((item) => item.id === value);

      detail.contact_address.zipcode = city?.zipcode || "";
    }

    // ใช้ที่อยู่บัตรประชาชนเป็นที่อยู่ติดต่อ หรือ ล้างข้อมูลที่อยู่ติดต่อ
    if (name === "contact_address.use_id_card_address_as_contact") {
      detail.contact_address = {
        ...detail.contact_address,
        ...(value
          ? detail.id_card_address // คัดลอกที่อยู่จากบัตรประชาชน
          : {
              city: "",
              district: "",
              name: "",
              no: "",
              province: "",
              road: "",
              street: "",
              substreet: "",
              zipcode: "",
            }),
      };
    }

    // เมื่อเปลี่ยนสังกัด -> ล้างฝ่ายที่จัดการ
    if (name === "employment.affiliate") {
      detail.employment.managed_division = "";
    }

    if (name === "employment.isManager") {
      detail.employment.managed_division = "";
    }

    // เลือกเป็นแพทย์
    if (name === "employment.isDoctor") {
      updateDoctorState(value);
      detail.employment.isNoneDoctor = false;
    }

    // หากเป็นแพทย์ที่บันทึกแล้วจะแก้ไขไม่ได้
    if (name === "employment.isDoctor" && !value && detail.employment.doctor_id) {
      return false; // return false เพื่อบอกว่าไม่ควรอัพเดท
    }

    // เลือกไม่ใช่หมอ
    if (name === "employment.isNoneDoctor") {
      if (value) {
        updateDoctorState(false);
      } else {
        detail.employment.licence = "";
      }
    }

    // อัพเดทสถานะหมอ/ไม่ใช่หมอ ตามตำแหน่งงาน (ถ้าไม่มี doctor_id)
    if (name === "employment.position" && !detail.employment.doctor_id) {
      const position = getPositionFlag(value);
      const isDoctor =
        position.teacher_flag ||
        (!position.teacher_flag && !position.certificate_flag && !position.student_flag);

      updateDoctorState(isDoctor);
    }

    return true; // return true เพื่อบอกว่าควรอัพเดท
  };

  const handleChangeValue = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const name: string = data.name;
    const value = data.type === "checkbox" ? data.checked : data.value;
    const detail: any = { ...props.EmployeeInfoSequence };
    const [first, second] = name.split(".");

    const shouldUpdate = validateFieldChanges(name, value, detail);

    if (!shouldUpdate) {
      return;
    }

    detail[first] = second
      ? {
          ...detail[first],
          [second]: value,
        }
      : value;

    props.setProp("EmployeeInfoSequence", { ...detail });
  };

  const handleAddMember = async () => {
    if (!hasEmptyMember) {
      await props.setProp("EmployeeInfoSequence.personal.family", [
        ...family,
        { active: true, birth_date: "", cid: "", name: "", type: "CHILD" },
      ]);
    }
  };

  const handleDeleteMember = (index: number) => () => {
    const targetMember = family[index];
    const isLastMember = filteredChildren.length === 1;
    const isEmptyMember = !targetMember.birth_date && !targetMember.cid && !targetMember.name;
    const shouldKeepActive = isLastMember && isEmptyMember;

    handleChangeMember(index)(null, {
      name: "active",
      value: shouldKeepActive,
    });

    if (isLastMember) {
      void handleAddMember();
    }
  };

  const handleChangeMember = (index: number) => (e: SyntheticEvent | null, data: any) => {
    (family as any)[index][data.name] = data.value;

    props.setProp(`EmployeeInfoSequence.personal.family`, [...family]);
  };

  const validateAllEmployeeData = () => {
    const basic = props.EmployeeInfoSequence?.basic;
    const personal = props.EmployeeInfoSequence?.personal;
    const employment = props.EmployeeInfoSequence?.employment;
    const idCardAddress = props.EmployeeInfoSequence?.id_card_address;
    const contactAddress = props.EmployeeInfoSequence?.contact_address;

    // ========== BASIC REQUIRED FIELDS ==========
    const basicRequiredFields = {
      code: "กรุณาระบุ Employee Code",
      email: "กรุณาระบุ E-mail",
      first_name_en: "กรุณาระบุ Name",
      last_name_en: "กรุณาระบุ Lastname",
      nationality: "กรุณาระบุ Nationality",
      phone_no: "กรุณาระบุ Mobile Number",
      pre_name_en: "กรุณาระบุ Pre-name",
      religion: "กรุณาระบุ Religion",
      ...(basic?.citizen_type === "T" && {
        citizen_no: "กรุณาระบุ เลขบัตรประจำตัวประชาชน",
        first_name: "กรุณาระบุ First name",
        last_name: "กรุณาระบุ Last name",
        pre_name: "กรุณาระบุ Pre name",
      }),
      ...(basic?.citizen_type === "F" && {
        passport_no: "กรุณาระบุ Passport Number",
      }),
    };

    // ========== PERSONAL FIELDS ==========
    const personalFields = {
      marital_status: "กรุณาระบุ Marital Status",
    };

    // ========== ADDRESS REQUIRED FIELDS ==========
    const addressRequiredFields = {
      city: "กรุณาระบุ Sub-district/Sub-area",
      district: "กรุณาระบุ District/Area",
      no: "กรุณาระบุ House number",
      province: "กรุณาระบุ Province",
      road: "กรุณาระบุ Street",
      zipcode: "กรุณาระบุ Postal Code",
    };

    // ========== CONTACT ADDRESS FIELDS ==========
    const contactAddressFields = {
      emergency_contact_name: "กรุณาระบุ Emergency Contact Name",
      emergency_contact_tel: "กรุณาระบุ Emergency Contact Number",
      ...(!contactAddress?.use_id_card_address_as_contact && addressRequiredFields),
    };

    // ========== EMPLOYMENT FIELDS ==========
    const employmentFields = {
      employment_type: "กรุณาระบุ Employment type",
      flag_end_date: "กรุณาระบุ Employment end date",
      flag_start_date: "กรุณาระบุ Employment start date",
      position: "กรุณาระบุ Position",
      ...(employment?.isDoctor && {
        certificate_no: "กรุณาระบุ Medical license",
        doctor_code: "กรุณาระบุ Doctor code",
        specialties: "กรุณาระบุ Specialty",
      }),
      ...(employment?.isNoneDoctor && {
        licence: "กรุณาระบุ Professional License",
      }),
    };

    const allRequiredFields = {
      ...(filteredSectionList.includes("Basic Employee Information") && basicRequiredFields),
      ...(filteredSectionList.includes("Personal Information") && personalFields),
      ...(filteredSectionList.includes("Employment Information") && employmentFields),
    };

    const errors = getRequireKeyError(
      allRequiredFields,
      { ...basic, ...personal, ...employment },
      intl
    );

    const idCardAddressErrors = getRequireKeyError(
      filteredSectionList.includes("Address on ID Card") ? addressRequiredFields : {},
      idCardAddress,
      intl
    );

    const contactAddressErrors = getRequireKeyError(
      filteredSectionList.includes("Contact Information") ? contactAddressFields : {},
      contactAddress,
      intl
    );

    // ========== COMBINE ALL ERRORS ==========
    const allErrors = { ...errors };

    if (Object.keys(idCardAddressErrors).length > 0) {
      allErrors.id_card_address = idCardAddressErrors;
    }

    if (Object.keys(contactAddressErrors).length > 0) {
      allErrors.contact_address = contactAddressErrors;
    }

    const isEndBeforeStart = beStringToMoment(employment?.flag_end_date || "").isBefore(
      beStringToMoment(employment?.flag_start_date || "")
    );

    if (
      !allErrors.flag_start_date &&
      !allErrors.flag_end_date &&
      employment?.flag_start_date &&
      employment.flag_end_date &&
      isEndBeforeStart
    ) {
      allErrors.flag_end_date = "กรุณาระบุ end date ให้มากกว่า start date";
    }

    const isValidDateFormat = isValidAdBeDate(basic?.birth_date);
    const isBirthDateInFuture = convertAdBeDateToMoment(basic?.birth_date)?.isAfter(
      moment(),
      "days"
    );

    if (!basic?.birth_date) {
      allErrors.birth_date = "กรุณาระบุ Date of Birth";
    }

    if (basic?.birth_date && !isValidDateFormat) {
      allErrors.birth_date = "กรุณาระบุรูปแบบวันที่ให้ถูกต้อง";
    }

    if (basic?.birth_date && isValidDateFormat && isBirthDateInFuture) {
      allErrors.birth_date = "กรุณาระบุวันที่ให้ถูกต้อง";
    }

    return allErrors;
  };

  const validateEducationData = () => {
    const errors: Record<string, string> = {};

    if (!educationData.graduationStatus) {
      errors.graduationStatus = "กรุณาระบุสถานภาพการศึกษา";
    }

    if (educationData.graduationStatus === "GRADUATED" && !educationData.graduationYear) {
      errors.graduationYear = "กรุณาระบุปีที่สำเร็จการศึกษา";
    }

    if (!educationData.educationLevel) {
      errors.educationLevel = "กรุณาระบุระดับการศึกษา";
    }

    if (!educationData.category) {
      errors.category = "กรุณาระบุหมวดการศึกษา";
    }

    if (!educationData.institution?.trim()) {
      errors.institution = "กรุณาระบุชื่อสถานศึกษา";
    }

    if (!educationData.faculty?.trim()) {
      errors.faculty = "กรุณาระบุชื่อคณะ";
    }

    if (!educationData.major?.trim()) {
      errors.major = "กรุณาระบุชื่อสาขา";
    }

    if (!educationData.degreeName?.trim()) {
      errors.degreeName = "กรุณาระบุชื่อปริญญา";
    }

    if (educationData.graduationYear && !graduationYearOptions.some(opt => opt.value === educationData.graduationYear)) {
      errors.graduationYear = "ปีที่สำเร็จการศึกษาไม่ถูกต้อง";
    }

    if (educationData.educationLevel && !EDUCATION_LEVEL_OPTIONS.some(opt => opt.value === educationData.educationLevel)) {
      errors.educationLevel = "ระดับการศึกษาไม่ถูกต้อง";
    }

    if (educationData.category && !educationCategoryOptions.some((opt: { value: null; }) => opt.value === educationData.category)) {
      errors.category = "หมวดการศึกษาไม่ถูกต้อง";
    }

    return errors;
  };

  const validateExperienceData = () => {
    const errors: Record<string, string> = {};

    if (!experienceData.startMonth) {
      errors.startMonth = "กรุณาระบุเดือนที่เริ่มงาน";
    }

    if (!experienceData.startYear) {
      errors.startYear = "กรุณาระบุปีที่เริ่มงาน";
    }

    if (!experienceData.endMonth) {
      errors.endMonth = "กรุณาระบุเดือนที่สิ้นสุดงาน";
    }

    if (!experienceData.endYear) {
      errors.endYear = "กรุณาระบุปีที่สิ้นสุดงาน";
    }

    if (!experienceData.companyName?.trim()) {
      errors.companyName = "กรุณาระบุชื่อบริษัท";
    }

    if (!experienceData.companyAddress?.trim()) {
      errors.companyAddress = "กรุณาระบุที่อยู่บริษัท";
    }

    if (!experienceData.position?.trim()) {
      errors.position = "กรุณาระบุตำแหน่งงาน";
    }

    if (experienceData.startYear && !experienceYearOptions.some(opt => opt.value === experienceData.startYear)) {
      errors.startYear = "ปีที่เริ่มงานไม่ถูกต้อง";
    }

    if (experienceData.endYear && !experienceYearOptions.some(opt => opt.value === experienceData.endYear)) {
      errors.endYear = "ปีที่สิ้นสุดงานไม่ถูกต้อง";
    }

    if (experienceData.startMonth && !MONTH_OPTIONS.some(opt => opt.value === experienceData.startMonth)) {
      errors.startMonth = "เดือนที่เริ่มงานไม่ถูกต้อง";
    }

    if (experienceData.endMonth && !MONTH_OPTIONS.some(opt => opt.value === experienceData.endMonth)) {
      errors.endMonth = "เดือนที่สิ้นสุดงานไม่ถูกต้อง";
    }

    if (experienceData.jobLevel && !JOB_LEVEL_OPTIONS.some(opt => opt.value === experienceData.jobLevel)) {
      errors.jobLevel = "ระดับงานไม่ถูกต้อง";
    }

    if (experienceData.startYear && experienceData.endYear) {
      if (experienceData.endYear < experienceData.startYear ||
          (experienceData.endYear === experienceData.startYear && experienceData.endMonth && experienceData.startMonth && experienceData.endMonth < experienceData.startMonth)) {
        errors.endYear = "วันที่สิ้นสุดต้องมากกว่าวันที่เริ่ม";
      }
    }

    return errors;
  };

  const handleSave = () => {
    const allErrors = validateAllEmployeeData();

    setShowRequiredField(allErrors);

    props.setProp(`errorMessage.${BTN_ACTS.SAVE}`, null);

    if (Object.keys(allErrors).length === 0) {
      const seq = props.EmployeeInfoSequence;
      const originalEmail = seq?.basic?.email;
      const newEmail = seq?.user?.email;

      if (originalEmail && newEmail && originalEmail !== newEmail) {
        setShowEmailChangeModal(true);
      } else {
        props.runSequence({
          sequence: "EmployeeInfo",
          action: ACTIONS.SAVE,
          params: { sendNewPasswordEmail: false },
        });
      }
    }
  };

  const handleSaveNewImage = async (src: string) => {
    const file = src ? convertBase64ToFile(src) : null;

    props.setProp("EmployeeInfoSequence.user", {
      ...props.EmployeeInfoSequence?.user,
      image: file,
    });
  };

  const handleClickUpload = () => {
    fileRef.current?.click();
  };

  const handleChangeFile = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.[0]) {
      return;
    }

    props.setProp("EmployeeInfoSequence.user", {
      ...props.EmployeeInfoSequence?.user,
      image: event.target.files[0],
    });
  };

  const handleChangeSpouseName = (e: SyntheticEvent, data: InputProps) => {
    const spouseIndex = family.findIndex((item) => item.type === "SPOUSE");

    if (spouseIndex === -1) {
      props.setProp("EmployeeInfoSequence.personal.family", [
        ...family,
        { active: true, birth_date: "", cid: "", name: data.value, type: "SPOUSE" },
      ]);
    } else {
      family[spouseIndex] = {
        ...family[spouseIndex],
        active: !!data.value,
        name: data.value,
      };

      props.setProp(`EmployeeInfoSequence.personal.family`, [...family]);
    }
  };

  const handleOpenResetPassword = () => {
    setOpenResetPassword(true);
  };

  const handleCloseResetPassword = () => {
    setOpenResetPassword(false);
  };

  const handleApprovalPasswordSuccess = (isApprove: boolean) => {
    props.setProp("EmployeeInfoSequence.user.use_sec_pass_for_approve", isApprove);
  };

  const handleToggleCurrentAddress = (e: SyntheticEvent, checked: boolean) => {
    handleChangeValue(null, {
      name: "contact_address.use_id_card_address_as_contact",
      value: checked,
    });

    if (Object.keys(showRequiredField.contact_address || {}).length > 0) {
      setShowRequiredField((prev) => ({
        ...prev,
        contact_address: {
          city: "",
          district: "",
          no: "",
          province: "",
          road: "",
          zipcode: "",
        } as any,
      }));
    }
  };

  const handleEditEducationInfo = (education: any, index: number) => {
    setEducationData({
        id: education.id,
        index: index,
        tempId: education.tempId,
        graduationStatus: education.status || education.graduationStatus,
        graduationYear: education.graduation_year || education.graduationYear,
        educationLevel: education.education_level || education.educationLevel,
        category: education.category,
        institution: education.institution,
        faculty: education.faculty,
        major: education.major,
        degreeName: education.degree_name || education.degreeName,
    });
    setShowRequiredEducationField({});
    setShowEducationInfoModal(true);
  }

  const handleEditExperienceInfo = (experience: any, index: number) => {
    setExperienceData({
        id: experience.id,
        index: index,
        tempId: experience.tempId,
        jobLevel: experience.job_level || experience.jobLevel,
        startMonth: experience.start_month || experience.startMonth,
        startYear: experience.start_year || experience.startYear,
        endMonth: experience.end_month || experience.endMonth,
        endYear: experience.end_year || experience.endYear,
        companyName: experience.company_name || experience.companyName,
        companyAddress: experience.company_address || experience.companyAddress,
        position: experience.position,
        jobType: experience.job_type || experience.jobType,
        jobDescription: experience.job_description || experience.jobDescription,
    });
    setShowRequiredExperienceField({});
    setShowExperienceInfoModal(true);
  }

  console.log("CardEmployeeInformation props:", props);

  return (
    <div
      style={{
        height: "calc(100vh - 7rem)",
        marginTop: "-1rem",
        overflowY: "auto",
        padding: "0px 10px 1rem",
      }}
    >
      <SnackMessage
        error={props.errorMessage?.[SEQUENCE_NAME]}
        success={null}
        onClose={() => {
          props.setProp(`errorMessage.${SEQUENCE_NAME}`, null);
        }}
      />

      <CardEmployeeInformationUX
        age={calculateAge}
        basic={props.EmployeeInfoSequence?.basic}
        contactAddress={props.EmployeeInfoSequence?.contact_address}
        currentDate={CURRENT_DATE}
        employment={props.EmployeeInfoSequence?.employment}
        filteredSectionList={filteredSectionList}
        idCardAddress={props.EmployeeInfoSequence?.id_card_address}
        isSingle={isSingle}
        personal={props.EmployeeInfoSequence?.personal}
        readOnly={props.readOnly}
        showRequiredField={showRequiredField}
        spouseName={spouseName}
        username={props.EmployeeInfoSequence?.username}
        languageUX={props.languageUX}
        contactCityOptions={contactCityOptions}
        contactDistrictOptions={contactDistrictOptions}
        currentEmpStatusOptions={props.masterOptions.currentEmpStatus}
        employmentTypeOptions={props.masterOptions.employmentTypeName}
        idCardCityOptions={idCardCityOptions}
        idCardDistrictOptions={idCardDistrictOptions}
        idOptions={IDENTITY_DOCUMENT_OPTIONS}
        managementOptions={managementOptions}
        maritalStatusOptions={props.masterOptions.employeeMaritalStatus}
        nationalityOptions={props.masterOptions.nationality}
        positionOptions={props.masterOptions.positionAll}
        prenameEnOptions={props.masterOptions.prenameEn}
        prenameThOptions={props.masterOptions.prenameTh}
        provinceOptions={props.masterOptions.province}
        religionOptions={props.masterOptions.religion}
        specialtyOptions={props.masterOptions.specialty}
        statusOptions={STATUS_OPTIONS}
        onChangeSpouseName={handleChangeSpouseName}
        onChangeValue={handleChangeValue}
        onResetPassword={handleOpenResetPassword}
        showEducationInfoModal={() => {
          if (!props.employeeId) {
            if (!hasShownPendingAlert) {
              setShowPendingAlert(true);
              setPendingAction('education');
              setHasShownPendingAlert(true);
            } else {
              setShowEducationInfoModal(true);
            }
          } else {
            setShowEducationInfoModal(true);
          }
        }}
        showExperienceInfoModal={() => {
          if (!props.employeeId) {
            if (!hasShownPendingAlert) {
              setShowPendingAlert(true);
              setPendingAction('experience');
              setHasShownPendingAlert(true);
            } else {
              setShowExperienceInfoModal(true);
            }
          } else {
            setShowExperienceInfoModal(true);
          }
        }}
        affiliationSearchBox={
          <TreeViewSearchBox
            id="EI"
            disabled={props.readOnly}
            items={affiliateItems}
            placeholder="Search your affiliation"
            value={props.EmployeeInfoSequence?.employment?.affiliate_name}
            onChange={handleChangeTreeview(props.EmployeeInfoSequence?.employment)}
            onParentSearch={handleParentSearchTreeview}
            onSearch={handleSearchTreeview}
          />
        }
        basicMaskInput={
          <MaskedInput
            mask="0-0000-00000-00-0"
            name="basic.citizen_no"
            readOnly={props.readOnly}
            value={props.EmployeeInfoSequence?.basic?.citizen_no}
            onChange={handleChangeValue}
          />
        }
        birthDate={
          <DateTextBox
            style={{ color: "rgba(0,0,0,.87)" }}
            disabled={props.readOnly}
            maxDate={CURRENT_DATE}
            value={props.EmployeeInfoSequence?.basic?.birth_date}
            autoAdBe
            useAdDate
            setLanguage="en"
            onChange={(value) => {
              handleChangeValue(null, { name: "basic.birth_date", value });
            }}
          />
        }
        buttonSave={
          !props.readOnly && (
            <ButtonLoadCheck
              setProp={props.setProp}
              color={props.EmployeeInfoSequence?.basic?.id ? "yellow" : "green"}
              paramKey={BTN_ACTS.SAVE}
              size="small"
              title={props.EmployeeInfoSequence?.basic?.id ? "Edit & Save" : "SAVE"}
              buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SAVE]}
              onClick={handleSave}
            />
          )
        }
        childrenList={filteredChildren.map((item, index) => {
          const isLast = index === filteredChildren.length - 1;

          return (
            <FormGroup
              key={`children-${item.index}`}
              style={{ marginBottom: isLast ? "0" : "" }}
              inline
            >
              <FormField width={4} inline>
                <label style={{ margin: 0, width: "170px" }}>Name Children</label>
                <Input
                  disabled={props.readOnly}
                  name="name"
                  value={item.name}
                  fluid
                  onChange={handleChangeMember(item.index)}
                />
              </FormField>
              <FormField width={4} inline>
                <label style={{ minWidth: "max-content" }}>ID Card</label>
                <MaskedInput
                  mask="0-0000-00000-00-0"
                  name="cid"
                  readOnly={props.readOnly}
                  value={item.cid}
                  onChange={handleChangeMember(item.index)}
                />
              </FormField>
              <FormField width={4} inline>
                <label style={{ minWidth: "max-content" }}>Date of Birth</label>
                <DateTextBox
                  disabled={props.readOnly}
                  maxDate={CURRENT_DATE}
                  value={item.birth_date}
                  onChange={(value: string) => {
                    handleChangeMember(item.index)(null, { name: "birth_date", value });
                  }}
                />
              </FormField>
              <FormField>
                <Button
                  style={{ fontSize: "8px" }}
                  color="red"
                  disabled={props.readOnly}
                  icon="minus"
                  size="mini"
                  onClick={handleDeleteMember(item.index)}
                />
              </FormField>
              <FormField>
                {isLast && (
                  <Button
                    style={{ fontSize: "8px" }}
                    color="green"
                    disabled={hasEmptyMember || props.readOnly}
                    icon="plus"
                    size="mini"
                    onClick={handleAddMember}
                  />
                )}
              </FormField>
            </FormGroup>
          );
        })}
        currentAddress={
          <Android12Switch
            disabled={props.readOnly}
            label="Same as Address on ID Card"
            switchStyle={styles.switchStyle}
            value={props.EmployeeInfoSequence?.contact_address?.use_id_card_address_as_contact}
            onChange={handleToggleCurrentAddress}
          />
        }
        educationInfo={
          [
            ...(props.EmployeeInfoSequence?.education?.map((edu, index) => ({ ...edu, isPending: false, displayIndex: index + 1 })) || []),
            ...pendingEducation.map((edu, index) => ({ ...edu, isPending: true, displayIndex: (props.EmployeeInfoSequence?.education?.length || 0) + index + 1 }))
          ].map((edu) => {
            const levelText =
              EDUCATION_LEVEL_OPTIONS.find(
                item =>
                  item.value === edu.education_level ||
                  item.value === edu.educationLevel
              )?.text ||
              edu.education_level ||
              edu.educationLevel;


            return (
              <div
                key={edu.id || edu.tempId}
                style={{
                  backgroundColor: "rgba(254, 245, 245, 1)",
                  padding: "1rem",
                  marginBottom: "1rem",
                  borderRadius: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontWeight: "bold",
                }}
              >
                <div>
                  ลำดับที่ {edu.displayIndex} : {levelText} {edu.institution} {edu.faculty}
                </div>
                <div style={{ display: "flex", gap: "12px", marginRight: "32px" }}>
                  <Icon
                    className="edit"
                    style={{ cursor: "pointer", color: "gray" }}
                    onClick={() => handleEditEducationInfo(edu, edu.displayIndex)}
                  />
                  <Icon
                    className="trash alternate outline"
                    style={{ cursor: "pointer", color: "gray" }}
                    onClick={() => {
                      if (edu.isPending) {
                        setPendingEducation(prev => prev.filter(p => p.tempId !== edu.tempId));
                      } else {
                        setDeleteEducationId(edu.id ?? null);
                        setShowDeleteEducationConfirm(true);
                      }
                    }}
                  />
                </div>
              </div>
            );
          })
        }
        experienceInfo={
          [
            ...(props.EmployeeInfoSequence?.experience?.map((exp, index) => ({ ...exp, isPending: false, displayIndex: index + 1 })) || []),
            ...pendingExperience.map((exp, index) => ({ ...exp, isPending: true, displayIndex: (props.EmployeeInfoSequence?.experience?.length || 0) + index + 1 }))
          ].map((exp) => {
            const levelText =
              JOB_LEVEL_OPTIONS.find((item) => item.value === exp.job_level || exp.jobLevel)?.text ||
              exp.job_level || exp.jobLevel;

            return (
              <div
                key={exp.id || exp.tempId}
                style={{
                  backgroundColor: "rgba(254, 245, 245, 1)",
                  padding: "1rem",
                  marginBottom: "1rem",
                  borderRadius: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontWeight: "bold",
                }}
              >
                <div>
                  {exp.start_year || exp.startYear} - {exp.end_year || exp.endYear} : {exp.company_name || exp.companyName}
                </div>
                <div style={{ display: "flex", gap: "12px", marginRight: "32px" }}>
                  <Icon
                    className="edit"
                    style={{ cursor: "pointer", color: "gray" }}
                    onClick={() => handleEditExperienceInfo(exp, exp.displayIndex)}
                  />
                  <Icon
                    className="trash alternate outline"
                    style={{ cursor: "pointer", color: "gray" }}
                    onClick={() => {
                      if (exp.isPending) {
                        setPendingExperience(prev => prev.filter(p => p.tempId !== exp.tempId));
                      } else {
                        setDeleteExperienceId(exp.id ?? null);
                        setShowDeleteExperienceConfirm(true);
                      }
                    }}
                  />
                </div>
              </div>
            );
          })
        }
        flagEndDate={
          <DateTextBox
            style={{ color: "rgba(0,0,0,.87)" }}
            disabled={props.readOnly}
            forwardYear={40}
            value={props.EmployeeInfoSequence?.employment?.flag_end_date}
            onChange={(value) => {
              handleChangeValue(null, { name: "employment.flag_end_date", value });
            }}
          />
        }
        profileImage={
          <div>
            <div style={styles.boxProfile}>
              <Image
                className="image"
                style={styles.profile}
                size="small"
                src={profileImage || IMAGES.profile}
                onError={(e: any) => {
                  e.target.onerror = null;
                  e.target.src = IMAGES.profile;
                }}
              />
            </div>

            <div style={{ margin: "0.5rem 0 0 0.65rem" }}>
              <div style={{ display: "flex" }}>
                <input
                  ref={fileRef}
                  id="file"
                  style={{ display: "none" }}
                  accept="image/*"
                  type="file"
                  onChange={handleChangeFile}
                />
                <Button
                  color="blue"
                  disabled={props.readOnly}
                  size="small"
                  icon
                  onClick={handleClickUpload}
                >
                  <Icon name="upload" />
                  <label style={{ marginLeft: "6px" }}>Upload</label>
                </Button>
                <Button
                  style={{ marginLeft: "0.5rem", paddingTop: "6px" }}
                  color="yellow"
                  disabled={props.readOnly}
                  size="small"
                  icon
                  onClick={() => {
                    setOpenWebCam(true);
                  }}
                >
                  <Icon className="camera" size="large" />
                </Button>
              </div>
            </div>
          </div>
        }
      />

      <Modal open={openResetPassword} size="small">
        <ModChangePassword
          onEvent={props.onEvent}
          setProp={props.setProp}
          changePassword={props.changePassword}
          user={props.EmployeeInfoSequence?.user}
          buttonLoadCheck={props.buttonLoadCheck}
          errorMessage={props.errorMessage}
          successMessage={props.successMessage}
          onApprovalPasswordSuccess={handleApprovalPasswordSuccess}
          onClose={handleCloseResetPassword}
        />
      </Modal>

      {showEmailChangeModal && (
        <Modal
          size="tiny"
          open={showEmailChangeModal}
          onClose={() => setShowEmailChangeModal(false)}
          style={{ borderRadius: 0, position: "relative" }}
        >
          <Button
            icon="close"
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              border: "none",
              background: "transparent",
              color: "black",
              fontWeight: "bold",
              cursor: "pointer",
              padding: 0,
              minWidth: 0,
              fontSize: "20px",
            }}
            onClick={() => setShowEmailChangeModal(false)}
          />

          <Modal.Header
            style={{
              backgroundColor: "var(--primary-theme-color)",
              color: "white",
              borderRadius: 0,
              padding: "8px 16px",
            }}
          >
            Alert message
          </Modal.Header>

          <Modal.Content style={{ textAlign: "center", fontWeight: "bold" }}>
            <p>We've detected a change in your email address.</p>
            <p>Would you like us to send the password to the updated email?</p>

            <div
              style={{ marginTop: "30px", display: "flex", justifyContent: "center", gap: "16px" }}
            >
              <ButtonLoadCheck
                title="Save & Send"
                color="green"
                style={{
                  backgroundColor: "white",
                  color: "#00ab41",
                  border: "2px solid #00ab41",
                  borderRadius: "6px",
                  fontWeight: 600,
                  minWidth: "120px",
                }}
                onClick={() => {
                  if (currentLoadingButton) return;
                  setCurrentLoadingButton("SEND");

                  const allErrors = validateAllEmployeeData();
                  setShowRequiredField(allErrors);
                  props.setProp(`errorMessage.${BTN_ACTS.SAVE}`, null);

                  if (Object.keys(allErrors).length === 0) {
                    props.runSequence({
                      sequence: "EmployeeInfo",
                      action: ACTIONS.SAVE,
                      params: { sendNewPasswordEmail: true },
                    });

                    setSuccessButton("SEND");
                    setTimeout(() => {
                      setShowEmailChangeModal(false);
                      setCurrentLoadingButton(null);
                    }, 700);
                  } else {
                    setCurrentLoadingButton(null);
                  }
                }}
                setProp={props.setProp}
                buttonLoadCheck={
                  currentLoadingButton === "SEND"
                    ? "LOADING"
                    : successButton === "SEND"
                    ? "SUCCESS"
                    : undefined
                }
              />

              <ButtonLoadCheck
                title="Save & Don't Send"
                color="blue"
                style={{
                  backgroundColor: "white",
                  color: "#3982b8",
                  border: "2px solid #3982b8",
                  borderRadius: "6px",
                  fontWeight: 600,
                  minWidth: "140px",
                }}
                onClick={() => {
                  if (currentLoadingButton) return;
                  setCurrentLoadingButton("DONT_SEND");

                  const allErrors = validateAllEmployeeData();
                  setShowRequiredField(allErrors);
                  props.setProp(`errorMessage.${BTN_ACTS.SAVE}`, null);

                  if (Object.keys(allErrors).length === 0) {
                    props.runSequence({
                      sequence: "EmployeeInfo",
                      action: ACTIONS.SAVE,
                      params: { sendNewPasswordEmail: false },
                    });

                    setSuccessButton("DONT_SEND");
                    setTimeout(() => {
                      setShowEmailChangeModal(false);
                      setCurrentLoadingButton(null);
                    }, 700);
                  } else {
                    setCurrentLoadingButton(null);
                  }
                }}
                setProp={props.setProp}
                buttonLoadCheck={
                  currentLoadingButton === "DONT_SEND"
                    ? "LOADING"
                    : successButton === "DONT_SEND"
                    ? "SUCCESS"
                    : undefined
                }
              />
            </div>
          </Modal.Content>
        </Modal>
      )}

      {showPendingAlert && (
        <Modal
          size="small"
          open={showPendingAlert}
          onClose={() => setShowPendingAlert(false)}
          style={{ borderRadius: 0, position: "relative" }}
        >
          <Modal.Header
            style={{
              backgroundColor: "var(--primary-theme-color)",
              color: "white",
              borderRadius: 0,
              padding: "8px 16px",
            }}
          >
            แจ้งเตือน
          </Modal.Header>

          <Modal.Content style={{ textAlign: "center", fontWeight: "bold" }}>
            <p>ข้อมูลการศึกษา/ประวัติการทำงานจะถูกบันทึกชั่วคราวและบันทึกจริงหลังจากบันทึกข้อมูลพนักงานแล้ว</p>
            <p>คุณต้องการดำเนินการต่อหรือไม่?</p>

            <div
              style={{ marginTop: "30px", display: "flex", justifyContent: "center", gap: "16px" }}
            >
              <Button
                onClick={() => {
                  setShowPendingAlert(false);
                  if (pendingAction === 'education') {
                    setShowEducationInfoModal(true);
                  } else if (pendingAction === 'experience') {
                    setShowExperienceInfoModal(true);
                  }
                  setPendingAction(null);
                }}
              >
                ดำเนินการต่อ
              </Button>

              <Button
                onClick={() => {
                  setShowPendingAlert(false);
                  setPendingAction(null);
                }}
              >
                ยกเลิก
              </Button>
            </div>
          </Modal.Content>
        </Modal>
      )}

      {showEducationInfoModal && (
        <Modal
          size="large"
          open={showEducationInfoModal}
            onClose={() => {
              setEducationData({
                id: null,
                index: 0,
                graduationStatus: "STUDYING",
                graduationYear: null,
                educationLevel: "",
                category: null,
                institution: "",
                faculty: "",
                major: "",
                degreeName: "",
              });
              setShowRequiredEducationField({});
              setShowEducationInfoModal(false);
            }}
          style={{ borderRadius: 10, position: "relative" }}
        >
          <Modal.Header
            style={{
              color: "var(--primary-theme-color)",
              borderRadius: 10,
              paddingTop: "24px",
              paddingBottom: "0px",
              border: "none",
              boxShadow: "none",
              fontSize: "20px"
            }}
          >
            ลำดับที่ {educationData.index !== 0 
            ? educationData.index 
            : (props.EmployeeInfoSequence?.education?.length || 0) + pendingEducation.length + 1}
          </Modal.Header>

          <Modal.Content style={{ textAlign: "center", fontWeight: "bold", paddingBottom: "6rem" }}>
            <Form>
              <FormGroup>
                <FormField width={5} required style={{ textAlign: "left", marginRight: "12px" }}>
                  <label style={{ margin: 0, width: "170px", textAlign: "left", marginBottom: "12px" }}>
                    {props.languageUX === "en" ? "Graduation status" : "สถานภาพการศึกษา"}
                  </label>
                  <Button
                    style={{
                      width: "140px",
                      borderRadius: "10px",
                      backgroundColor: educationData.graduationStatus === "GRADUATED" ? "var(--primary-theme-color)" : "white",
                      color: educationData.graduationStatus === "GRADUATED" ? "white" : "gray",
                      border: educationData.graduationStatus === "GRADUATED" ? "1px solid var(--primary-theme-color)" : "1px solid gray",
                      marginRight: "24px",
                    }}
                    size="small"
                    onClick={() => setEducationData((prev) => ({ ...prev, graduationStatus: "GRADUATED" }))}
                  >
                    จบการศึกษา
                  </Button>
                  <Button
                    style={{
                      width: "140px",
                      borderRadius: "10px",
                      backgroundColor: educationData.graduationStatus === "STUDYING" ? "var(--primary-theme-color)" : "white",
                      color: educationData.graduationStatus === "STUDYING" ? "white" : "gray",
                      border: educationData.graduationStatus === "STUDYING" ? "1px solid var(--primary-theme-color)" : "1px solid gray",
                    }}
                    size="small"
                    onClick={() => setEducationData((prev) => ({ ...prev, graduationStatus: "STUDYING" }))}
                  >
                    กำลังศึกษา
                  </Button>
                </FormField>
                 <FormField
                   width={4}
                   required={educationData.graduationStatus === "GRADUATED"}
                   className={showRequiredEducationField?.graduationYear ? "error" : ""}
                   style={{ textAlign: "left", marginLeft: "20px", position: "relative" }}
                 >
                  <label style={{ margin: 0, width: "170px", textAlign: "left", marginBottom: "12px" }}>
                    {props.languageUX === "en" ? "Graduation year" : "ปีที่สำเร็จการศึกษา"}
                  </label>
                  <Dropdown
                    placeholder="-- เลือกปีที่สำเร็จการศึกษา --"
                    options={graduationYearOptions}
                    value={educationData.graduationYear ?? undefined}
                    onChange={handleEducationChange("graduationYear")}
                    scrolling
                    selection
                    clearable
                  />
                  <div
                    style={{position: "absolute", bottom: "-16px", color: "red", fontSize: "0.75rem", right: "15px"}}>
                    {showRequiredEducationField?.graduationYear || ""}
                  </div>
                </FormField>
              </FormGroup>
              <FormGroup>
                 <FormField width={4} required className={showRequiredEducationField?.educationLevel ? "error" : ""} style={{ textAlign: "left", position: "relative" }}>
                   <label style={{ margin: 0, width: "170px", textAlign: "left", marginBottom: "12px" }}>
                     {props.languageUX === "en" ? "Education level" : "ระดับการศึกษา"}
                   </label>
                  <Dropdown
                    placeholder="-- กรุณาระบุระดับการศึกษา --"
                    options={EDUCATION_LEVEL_OPTIONS}
                    value={educationData.educationLevel}
                    onChange={handleEducationChange("educationLevel")}
                    selection
                  />
                  <div
                    style={{position: "absolute", bottom: "-16px", color: "red", fontSize: "0.75rem", right: "15px"}}>
                    {showRequiredEducationField?.educationLevel || ""}
                  </div>
                </FormField>
                 <FormField width={4} required className={showRequiredEducationField?.category ? "error" : ""} style={{ textAlign: "left", position: "relative" }}>
                   <label style={{ margin: 0, width: "170px", textAlign: "left", marginBottom: "12px" }}>
                     {props.languageUX === "en" ? "Category" : "หมวดการศึกษา"}
                   </label>
                  <Dropdown
                    placeholder="-- กรุณาระบุหมวดการศึกษา --"
                    options={educationCategoryOptions}
                    value={educationData.category ?? undefined}
                    onChange={handleEducationChange("category")}
                    selection
                  />
                  <div
                    style={{position: "absolute", bottom: "-16px", color: "red", fontSize: "0.75rem", right: "15px"}}>
                    {showRequiredEducationField?.category || ""}
                  </div>
                </FormField>
                 <FormField width={8} required className={showRequiredEducationField?.institution ? "error" : ""} style={{ textAlign: "left", position: "relative" }}>
                   <label style={{ margin: 0, width: "170px", textAlign: "left", marginBottom: "12px" }}>
                     {props.languageUX === "en" ? "Institution" : "สถานศึกษา"}
                   </label>
                  <Input
                    name="institution"
                    placeholder="-- กรุณาระบุชื่อสถานศึกษา --"
                    value={educationData.institution}
                    onChange={handleEducationChange("institution")}
                  />
                  <div
                    style={{position: "absolute", bottom: "-16px", color: "red", fontSize: "0.75rem", right: "15px"}}>
                    {showRequiredEducationField?.institution || ""}
                  </div>
                </FormField>
              </FormGroup>
              <FormGroup>
                 <FormField width={5} required className={showRequiredEducationField?.faculty ? "error" : ""} style={{ textAlign: "left", position: "relative" }}>
                   <label style={{ margin: 0, width: "170px", textAlign: "left", marginBottom: "12px" }}>
                     {props.languageUX === "en" ? "Faculty" : "คณะ"}
                   </label>
                  <Input
                    name="faculty"
                    placeholder="-- กรุณาระบุชื่อคณะ --"
                    value={educationData.faculty}
                    onChange={handleEducationChange("faculty")}
                  />
                  <div
                    style={{position: "absolute", bottom: "-16px", color: "red", fontSize: "0.75rem", right: "15px"}}>
                    {showRequiredEducationField?.faculty || ""}
                  </div>
                </FormField>
                 <FormField width={5} required className={showRequiredEducationField?.major ? "error" : ""} style={{ textAlign: "left", position: "relative" }}>
                   <label style={{ margin: 0, width: "170px", textAlign: "left", marginBottom: "12px" }}>
                     {props.languageUX === "en" ? "Major" : "สาขา"}
                   </label>
                  <Input
                    name="major"
                    placeholder="-- กรุณาระบุชื่อสาขา --"
                    value={educationData.major}
                    onChange={handleEducationChange("major")}
                  />
                  <div
                    style={{position: "absolute", bottom: "-16px", color: "red", fontSize: "0.75rem", right: "15px"}}>
                    {showRequiredEducationField?.major || ""}
                  </div>
                </FormField>
                 <FormField width={6} required className={showRequiredEducationField?.degreeName ? "error" : ""} style={{ textAlign: "left", position: "relative" }}>
                   <label style={{ margin: 0, width: "170px", textAlign: "left", marginBottom: "12px" }}>
                     {props.languageUX === "en" ? "Degree name" : "ชื่อปริญญา"}
                   </label>
                  <Input
                    name="degree_name"
                    placeholder="-- กรุณาระบุชื่อปริญญา --"
                    value={educationData.degreeName}
                    onChange={handleEducationChange("degreeName")}
                  />
                  <div
                    style={{position: "absolute", bottom: "-16px", color: "red", fontSize: "0.75rem", right: "15px"}}>
                    {showRequiredEducationField?.degreeName || ""}
                  </div>
                </FormField>
              </FormGroup>
            </Form>
          </Modal.Content>
          <Modal.Actions style={{ justifyContent: "center", gap: "16px", border: "none", boxShadow: "none" }}>
              <Button
                onClick={() => {
                  setEducationData({
                    id: null,
                    index: 0,
                    graduationStatus: "STUDYING",
                    graduationYear: null,
                    educationLevel: "",
                    category: null,
                    institution: "",
                    faculty: "",
                    major: "",
                    degreeName: "",
                  });
                  setShowRequiredEducationField({});
                  setShowEducationInfoModal(false);
                }}>
                ปิดหน้าต่าง
              </Button>
              <ButtonLoadCheck
                title="บันทึกข้อมูล"
                style={{ backgroundColor: "var(--primary-theme-color)", color: "white" }}
                 onClick={() => {
                   const educationErrors = validateEducationData();
                   setShowRequiredEducationField(educationErrors);

                   if (Object.keys(educationErrors).length === 0) {
                     if (props.employeeId) {
                       setIsSavingEducation(true);
                       props.runSequence({
                         sequence: "EmployeeInfo",
                         action: ACTIONS.EDUCATION_SAVE,
                         params: { educationData, employeeId: props.employeeId },
                       });
                     } else {
                       if (educationData.tempId) {
                         setPendingEducation(prev => prev.map(p => p.tempId === educationData.tempId ? { ...educationData } : p));
                       } else {
                         const newEducation = { ...educationData, tempId: Date.now() };
                         setPendingEducation(prev => [...prev, newEducation]);
                       }
                       setEducationData({
                         id: null,
                         index: 0,
                         graduationStatus: "STUDYING",
                         graduationYear: null,
                         educationLevel: "",
                         category: null,
                         institution: "",
                         faculty: "",
                         major: "",
                         degreeName: "",
                       });
                       setShowRequiredEducationField({});
                       setShowEducationInfoModal(false);
                     }
                   }
                 }}
                setProp={props.setProp}
                buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.EDUCATION_SAVE]}
                paramKey={BTN_ACTS.EDUCATION_SAVE}
              />
          </Modal.Actions>
        </Modal>
      )}

      {showExperienceInfoModal && (
        <Modal
          size="small"
          open={showExperienceInfoModal}
          onClose={() => {
            setExperienceData({
              id: null,
              index: 0,
              jobLevel: "",
              startMonth: null,
              startYear: null,
              endMonth: null,
              endYear: null,
              companyName: "",
              companyAddress: "",
              position: "",
              jobType: "",
              jobDescription: "",
            });
            setShowRequiredExperienceField({});
            setShowExperienceInfoModal(false);
          }}
          style={{ borderRadius: 10, position: "relative" }}
        >
          <Modal.Header
            style={{
              color: "var(--primary-theme-color)",
              borderRadius: 10,
              paddingTop: "24px",
              paddingBottom: "0px",
              border: "none",
              boxShadow: "none",
              fontSize: "20px"
            }}
          >
            ลำดับที่ {experienceData.index !== 0
            ? experienceData.index
            : (props.EmployeeInfoSequence?.experience?.length || 0) + pendingExperience.length + 1}
          </Modal.Header>

          <Modal.Content style={{ textAlign: "center", fontWeight: "bold", paddingBottom: "3rem" }}>
            <Form>
              <FormGroup>
                <FormField width={8} required className={showRequiredExperienceField?.startMonth || showRequiredExperienceField?.startYear ? "error" : ""} style={{ textAlign: "left", position: "relative" }}>
                  <label style={{ margin: 0, width: "170px", textAlign: "left", marginBottom: "12px" }}>
                    {props.languageUX === "en" ? "Start since" : "วันที่เริ่มตั้งแต่"}
                  </label>
                  <div style={{ display: "flex", gap: "12px" }}>
                    <Dropdown
                      placeholder="เดือน"
                      options={MONTH_OPTIONS}
                      value={experienceData.startMonth ?? undefined}
                      onChange={handleExperienceChange("startMonth")}
                      style={{ minWidth: "150px" }}
                      selection
                    />
                    <div
                      style={{position: "absolute", bottom: "-16px", color: "red", fontSize: "0.75rem", left: "50px"}}>
                      {showRequiredExperienceField?.startMonth || ""}
                    </div>
                    <Dropdown
                      placeholder="ปี"
                      options={experienceYearOptions}
                      value={experienceData.startYear ?? undefined}
                      onChange={handleExperienceChange("startYear")}
                      style={{ minWidth: "150px" }}
                      selection
                    />
                    <div
                      style={{position: "absolute", bottom: "-16px", color: "red", fontSize: "0.75rem", right: "15px"}}>
                      {showRequiredExperienceField?.startYear || ""}
                    </div>
                  </div>
                </FormField>
                <FormField width={8} required className={showRequiredExperienceField?.endMonth || showRequiredExperienceField?.endYear ? "error" : ""} style={{ textAlign: "left", position: "relative" }}>
                  <label style={{ margin: 0, width: "170px", textAlign: "left", marginBottom: "12px" }}>
                    {props.languageUX === "en" ? "until" : "จนถึง"}
                  </label>
                  <div style={{ display: "flex", gap: "12px" }}>
                    <Dropdown
                      placeholder="เดือน"
                      options={MONTH_OPTIONS}
                      value={experienceData.endMonth ?? undefined}
                      onChange={handleExperienceChange("endMonth")}
                      selection
                      style={{ minWidth: "150px" }}
                    />
                    <div
                      style={{position: "absolute", bottom: "-16px", color: "red", fontSize: "0.75rem", left: "50px"}}>
                      {showRequiredExperienceField?.endMonth || ""}
                    </div>
                    <Dropdown
                      placeholder="ปี"
                      options={experienceYearOptions}
                      value={experienceData.endYear ?? undefined}
                      onChange={handleExperienceChange("endYear")}
                      selection
                      style={{ minWidth: "150px" }}
                    />
                    <div
                      style={{position: "absolute", bottom: "-16px", color: "red", fontSize: "0.75rem", right: "15px"}}>
                      {showRequiredExperienceField?.endYear || ""}
                    </div>
                  </div>
                </FormField>
              </FormGroup>
              <FormGroup>
                <FormField width={16} required className={showRequiredExperienceField?.companyName ? "error" : ""} style={{ textAlign: "left", position: "relative" }}>
                  <label style={{ margin: 0, width: "170px", textAlign: "left", marginBottom: "12px" }}>
                    {props.languageUX === "en" ? "Company Name" : "ชื่อบริษัท"}
                  </label>
                  <Input
                    name="companyName"
                    placeholder="-- กรุณาระบุชื่อบริษัท --"
                    value={experienceData.companyName}
                    onChange={handleExperienceChange("companyName")}
                  />
                  <div
                    style={{position: "absolute", bottom: "-16px", color: "red", fontSize: "0.75rem", right: "15px"}}>
                    {showRequiredExperienceField?.companyName || ""}
                  </div>
                </FormField>
              </FormGroup>
              <FormGroup>
                <FormField width={16} required className={showRequiredExperienceField?.companyAddress ? "error" : ""} style={{ textAlign: "left", position: "relative" }}>
                  <label style={{ margin: 0, width: "170px", textAlign: "left", marginBottom: "12px" }}>
                    {props.languageUX === "en" ? "Company Address" : "ที่อยู่บริษัท"}
                  </label>
                  <Input
                    name="companyAddress"
                    placeholder="-- กรุณาระบุที่อยู่บริษัท --"
                    value={experienceData.companyAddress}
                    onChange={handleExperienceChange("companyAddress")}
                  />
                  <div
                    style={{position: "absolute", bottom: "-16px", color: "red", fontSize: "0.75rem", right: "15px"}}>
                    {showRequiredExperienceField?.companyAddress || ""}
                  </div>
                </FormField>
              </FormGroup>
              <FormGroup>
                <FormField width={8} required className={showRequiredExperienceField?.position ? "error" : ""} style={{ textAlign: "left", position: "relative" }}>
                  <label style={{ margin: 0, width: "170px", textAlign: "left", marginBottom: "12px" }}>
                    {props.languageUX === "en" ? "Position" : "ตำแหน่งงาน"}
                  </label>
                  <Input
                    name="position"
                    placeholder="-- กรุณาระบุตำแหน่งงาน --"
                    value={experienceData.position}
                    onChange={handleExperienceChange("position")}
                  />
                  <div
                    style={{position: "absolute", bottom: "-16px", color: "red", fontSize: "0.75rem", right: "15px"}}>
                    {showRequiredExperienceField?.position || ""}
                  </div>
                </FormField>
                <FormField width={8} style={{ textAlign: "left" }}>
                  <label style={{ margin: 0, width: "170px", textAlign: "left", marginBottom: "12px" }}>
                    {props.languageUX === "en" ? "Job Type" : "ประเภทงาน"}
                  </label>
                  <Input
                    name="jobType"
                    placeholder="-- กรุณาระบุประเภทงาน --"
                    value={experienceData.jobType}
                    onChange={handleExperienceChange("jobType")}
                  />
                </FormField>
              </FormGroup>
              <FormGroup>
                <FormField width={8} className={showRequiredExperienceField?.jobLevel ? "error" : ""} style={{ textAlign: "left", position: "relative" }}>
                  <label style={{ margin: 0, width: "170px", textAlign: "left", marginBottom: "12px" }}>
                    {props.languageUX === "en" ? "Job Level" : "ระดับงาน"}
                  </label>
                  <Dropdown
                    placeholder="-- กรุณาระบุระดับงาน --"
                    options={JOB_LEVEL_OPTIONS}
                    value={experienceData.jobLevel}
                    onChange={handleExperienceChange("jobLevel")}
                    selection
                  />
                </FormField>
                <FormField width={8} className={showRequiredExperienceField?.jobDescription ? "error" : ""} style={{ textAlign: "left", position: "relative" }}>
                  <label style={{ margin: 0, width: "170px", textAlign: "left", marginBottom: "12px" }}>
                    {props.languageUX === "en" ? "Job Description" : "รายละเอียดงาน"}
                  </label>
                  <Form.TextArea
                    name="jobDescription"
                    placeholder="-- กรุณาระบุรายละเอียดงาน --"
                    value={experienceData.jobDescription}
                    onChange={handleExperienceChange("jobDescription")}
                  />
                </FormField>
              </FormGroup>
            </Form>
          </Modal.Content>
          <Modal.Actions style={{ justifyContent: "center", gap: "16px", border: "none", boxShadow: "none" }}>
            <Button
              onClick={() => {
                setExperienceData({
                  id: null,
                  index: 0,
                  jobLevel: "",
                  startMonth: null,
                  startYear: null,
                  endMonth: null,
                  endYear: null,
                  companyName: "",
                  companyAddress: "",
                  position: "",
                  jobType: "",
                  jobDescription: "",
                });
                setShowRequiredExperienceField({});
                setShowExperienceInfoModal(false);
              }}>
              ปิดหน้าต่าง
            </Button>
            <ButtonLoadCheck
              title="บันทึกข้อมูล"
              style={{ backgroundColor: "var(--primary-theme-color)", color: "white" }}
              onClick={() => {
                const experienceErrors = validateExperienceData();
                setShowRequiredExperienceField(experienceErrors);

                if (Object.keys(experienceErrors).length === 0) {
                  if (props.employeeId) {
                    setIsSavingExperience(true);
                    props.runSequence({
                      sequence: "EmployeeInfo",
                      action: ACTIONS.EXPERIENCE_SAVE,
                      params: { experienceData, employeeId: props.employeeId },
                    });
                  } else {
                    if (experienceData.tempId) {
                      setPendingExperience(prev => prev.map(p => p.tempId === experienceData.tempId ? { ...experienceData } : p));
                    } else {
                      const newExperience = { ...experienceData, tempId: Date.now() };
                      setPendingExperience(prev => [...prev, newExperience]);
                    }
                    setExperienceData({
                      id: null,
                      index: 0,
                      jobLevel: "",
                      startMonth: null,
                      startYear: null,
                      endMonth: null,
                      endYear: null,
                      companyName: "",
                      companyAddress: "",
                      position: "",
                      jobType: "",
                      jobDescription: "",
                    });
                    setShowRequiredExperienceField({});
                    setShowExperienceInfoModal(false);
                  }
                }
              }}
              setProp={props.setProp}
              buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.EXPERIENCE_SAVE]}
              paramKey={BTN_ACTS.EXPERIENCE_SAVE}
            />
          </Modal.Actions>
        </Modal>
      )}

      <Confirm
        open={showDeleteEducationConfirm}
        header="ยืนยันการลบข้อมูลการศึกษา"
        content={
          deleteEducationId &&
            (() => {
              const educationList = props.EmployeeInfoSequence?.education;
              if (!educationList) return null;
              const edu = educationList.find(e => e.id === deleteEducationId);
              if (!edu) return null;
              const levelText =
                EDUCATION_LEVEL_OPTIONS.find(item => item.value === edu.education_level)?.text ||
                edu.education_level;
              const displayIndex = educationList.findIndex(e => e.id === deleteEducationId) + 1;
              return `คุณต้องการลบข้อมูลการศึกษา ลำดับที่ ${displayIndex} : ${levelText} ${edu.institution} ${edu.faculty} หรือไม่?`;
            })()
        }
        cancelButton={{
          content: "ยกเลิก",
          style: {
            borderRadius: "8px",
            width: "80px"
          }
        }}
        confirmButton={{
          content: "ลบ",
          style: {
            backgroundColor: "var(--primary-theme-color)",
            color: "white",
            borderRadius: "8px",
            width: "80px"
          }
        }}
        onCancel={() => {
          setShowDeleteEducationConfirm(false);
          setDeleteEducationId(null);
        }}
        onConfirm={() => {
          if (deleteEducationId) {
            props.runSequence({
              sequence: "EmployeeInfo",
              action: ACTIONS.EDUCATION_DELETE,
              params: { educationId: deleteEducationId, employeeId: props.employeeId }
            });
          }
          setShowDeleteEducationConfirm(false);
          setDeleteEducationId(null);
        }}
      />

      <Confirm
        open={showDeleteExperienceConfirm}
        header="ยืนยันการลบข้อมูลประวัติการทำงาน"
        content={
          deleteExperienceId &&
            (() => {
              const experienceList = props.EmployeeInfoSequence?.experience;
              if (!experienceList) return null;
              const exp = experienceList.find(e => e.id === deleteExperienceId);
              if (!exp) return null;
              return `คุณต้องการลบข้อมูลประวัติการทำงาน ${exp.start_year} - ${exp.end_year} : ${exp.company_name} หรือไม่?`;
            })()
        }
        cancelButton={{
          content: "ยกเลิก",
          style: {
            borderRadius: "8px",
            width: "80px"
          }
        }}
        confirmButton={{
          content: "ลบ",
          style: {
            backgroundColor: "var(--primary-theme-color)",
            color: "white",
            borderRadius: "8px",
            width: "80px"
          }
        }}
        onCancel={() => {
          setShowDeleteExperienceConfirm(false);
          setDeleteExperienceId(null);
        }}
        onConfirm={() => {
          if (deleteExperienceId) {
            props.runSequence({
              sequence: "EmployeeInfo",
              action: ACTIONS.EXPERIENCE_DELETE,
              params: { experienceId: deleteExperienceId, employeeId: props.employeeId }
            });
          }
          setShowDeleteExperienceConfirm(false);
          setDeleteExperienceId(null);
        }}
      />

      <WebcamProfile
        open={openWebCam}
        setNewImage={handleSaveNewImage}
        onClose={() => {
          setOpenWebCam(false);
        }}
      />
    </div>
  );
};

CardEmployeeInformation.displayName = "CardEmployeeInformation";

export default React.memo(CardEmployeeInformation);
